import React, { useEffect, useMemo, useState } from 'react';
import api from '../../api/client';
import { useNavigate } from 'react-router-dom';
import {
  Clock, Users, AlertCircle, CheckCircle, ChevronRight, PauseCircle,
  SearchIcon, Briefcase, FileText, UserCheck, ThumbsUp, XCircle, TrendingUp
} from 'lucide-react';

interface Vacancy {
  id: string;
  title: string;
  status: string;
  department: string;
  candidatesCount: number;
  createdAt: string;
  closedAt?: string;
  fee?: number;
  timeToFill: number | null;
}

const statusConfig: Record<string, { label: string; color: string; bg: string; icon: React.ReactNode }> = {
  search: { label: 'Поиск', color: '#2575fc', bg: 'rgba(37,117,252,0.1)', icon: <SearchIcon size={16} /> },
  in_progress: { label: 'В работе', color: '#6a11cb', bg: 'rgba(106,17,203,0.1)', icon: <PauseCircle size={16} /> },
  resume_at_client: { label: 'Резюме у клиента', color: '#3498db', bg: 'rgba(52,152,219,0.1)', icon: <FileText size={16} /> },
  interview_at_client: { label: 'Интервью у клиента', color: '#e67e22', bg: 'rgba(230,126,34,0.1)', icon: <UserCheck size={16} /> },
  offer: { label: 'Оффер', color: '#2ecc71', bg: 'rgba(46,204,113,0.1)', icon: <ThumbsUp size={16} /> },
  placement: { label: 'Плейсмент', color: '#27ae60', bg: 'rgba(39,174,96,0.1)', icon: <CheckCircle size={16} /> },
  closed_by_client: { label: 'Закрыта клиентом', color: '#e74c3c', bg: 'rgba(231,76,60,0.1)', icon: <XCircle size={16} /> },
  cancelled: { label: 'Отменена', color: '#95a5a6', bg: 'rgba(149,165,166,0.1)', icon: <AlertCircle size={16} /> },
};

const Vacancies: React.FC = () => {
  const navigate = useNavigate();
  const [vacancies, setVacancies] = useState<Vacancy[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [sortKey, setSortKey] = useState<string>('createdAt');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchVacancies = async () => {
      try {
        const res = await api.get('/vacancies');
        const data = res.data.map((v: any) => ({
          ...v,
          id: v.id || v._id,
          timeToFill: v.closedAt
            ? Math.ceil((new Date(v.closedAt).getTime() - new Date(v.createdAt).getTime()) / (1000 * 60 * 60 * 24))
            : v.status && !['placement', 'closed_by_client', 'cancelled'].includes(v.status)
            ? Math.ceil((new Date().getTime() - new Date(v.createdAt).getTime()) / (1000 * 60 * 60 * 24))
            : null,
        }));
        setVacancies(data);
        setError(null);
      } catch (err) {
        console.error('Ошибка загрузки вакансий:', err);
        setError('Не удалось загрузить вакансии.');
      } finally {
        setLoading(false);
      }
    };
    fetchVacancies();
  }, []);

  const avgDaysOpen = useMemo(() => {
    const openVac = vacancies.filter(v => !['placement', 'closed_by_client', 'cancelled'].includes(v.status));
    if (openVac.length === 0) return 0;
    return Math.round(openVac.reduce((sum, v) => sum + (v.timeToFill || 0), 0) / openVac.length);
  }, [vacancies]);

  const filtered = useMemo(() => {
    let list = filterStatus === 'all' ? vacancies : vacancies.filter(v => v.status === filterStatus);
    list.sort((a: any, b: any) => {
      if (sortKey === 'title') return a.title.localeCompare(b.title);
      if (sortKey === 'timeToFill') return (b.timeToFill || 0) - (a.timeToFill || 0);
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
    return list;
  }, [vacancies, filterStatus, sortKey]);

  if (loading) return <div style={{ padding: 60, textAlign: 'center' }}>Загрузка вакансий...</div>;
  if (error) return <div style={{ padding: 60, textAlign: 'center', color: '#ef4444' }}><p>{error}</p></div>;

  const total = vacancies.length;
  const openCount = vacancies.filter(v => !['placement', 'closed_by_client', 'cancelled'].includes(v.status)).length;
  const closedCount = vacancies.filter(v => ['placement', 'closed_by_client'].includes(v.status)).length;

  return (
    <div style={{ padding: '0 20px 40px 20px', fontFamily: 'Inter, sans-serif' }}>
      
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 48, height: 48, borderRadius: 14, background: 'linear-gradient(135deg, #6a11cb, #2575fc)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', boxShadow: '0 8px 20px rgba(106,17,203,0.3)' }}>
            <Briefcase size={24} />
          </div>
          <div>
            <h1 style={{ fontSize: 28, fontWeight: 800, color: '#1a1a2e', margin: 0 }}>Вакансии</h1>
            <p style={{ color: '#64748b', margin: '4px 0 0 0', fontSize: 14 }}>Управление открытыми позициями</p>
          </div>
        </div>
      </div>

      {/* KPI */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiIcon, background: 'rgba(37,117,252,0.1)', color: '#2575fc' }}><Briefcase size={22} /></div>
          <div><div style={styles.kpiLabel}>Всего</div><div style={styles.kpiValue}>{total}</div></div>
        </div>
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiIcon, background: 'rgba(245,158,11,0.1)', color: '#f59e0b' }}><Clock size={22} /></div>
          <div><div style={styles.kpiLabel}>В работе</div><div style={styles.kpiValue}>{openCount}</div></div>
        </div>
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiIcon, background: 'rgba(16,185,129,0.1)', color: '#10b981' }}><CheckCircle size={22} /></div>
          <div><div style={styles.kpiLabel}>Закрыто</div><div style={styles.kpiValue}>{closedCount}</div></div>
        </div>
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiIcon, background: 'rgba(106,17,203,0.1)', color: '#6a11cb' }}><TrendingUp size={22} /></div>
          <div><div style={styles.kpiLabel}>Ср. срок</div><div style={styles.kpiValue}>{avgDaysOpen} дн.</div></div>
        </div>
      </div>

      {/* Фильтры-таблетки */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 4, background: 'rgba(255,255,255,0.5)', padding: 4, borderRadius: 12, border: '1px solid rgba(255,255,255,0.6)' }}>
          <button onClick={() => setFilterStatus('all')} style={{ ...styles.filterBtn, background: filterStatus === 'all' ? '#2575fc' : 'transparent', color: filterStatus === 'all' ? '#fff' : '#64748b' }}>Все</button>
          {Object.entries(statusConfig).map(([key, cfg]) => (
            <button key={key} onClick={() => setFilterStatus(key)} style={{ ...styles.filterBtn, background: filterStatus === key ? '#2575fc' : 'transparent', color: filterStatus === key ? '#fff' : '#64748b' }}>{cfg.label}</button>
          ))}
        </div>
        <select value={sortKey} onChange={e => setSortKey(e.target.value)} style={styles.selectStyle}>
          <option value="createdAt">По дате создания</option>
          <option value="title">По названию</option>
          <option value="timeToFill">По сроку</option>
        </select>
      </div>

      {/* Таблица */}
      <div style={styles.glassCard}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={styles.th}>Название</th>
                <th style={styles.th}>Статус</th>
                <th style={styles.th}>Кандидатов</th>
                <th style={styles.th}>Дней</th>
                <th style={styles.th}>Гонорар</th>
                <th style={styles.th}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Нет вакансий</td></tr>
              ) : (
                filtered.map(vac => {
                  const config = statusConfig[vac.status] || statusConfig.search;
                  const isStale = !['placement', 'closed_by_client', 'cancelled'].includes(vac.status) && vac.timeToFill != null && avgDaysOpen > 0 && vac.timeToFill > avgDaysOpen * 1.5;
                  return (
                    <tr key={vac.id} onClick={() => navigate(`/app/recruitment-analytics/vacancies/${vac.id}`)} style={{ cursor: 'pointer', transition: 'background 0.2s' }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(37,117,252,0.03)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}>
                      <td style={styles.td}><strong>{vac.title}</strong></td>
                      <td style={styles.td}>
                        <span style={{ ...styles.badge, background: config.bg, color: config.color }}>
                          {config.icon} {config.label}
                        </span>
                      </td>
                      <td style={styles.td}><Users size={14} style={{ marginRight: 4, color: '#64748b' }} />{vac.candidatesCount}</td>
                      <td style={styles.td}>
                        <Clock size={14} style={{ marginRight: 4, color: '#64748b' }} />
                        {vac.timeToFill != null ? `${vac.timeToFill} дн.` : '—'}
                        {isStale && <span style={{ color: '#f59e0b', fontWeight: 700, marginLeft: 6 }}>⚠</span>}
                      </td>
                      <td style={styles.td}>{vac.fee != null ? `${vac.fee.toLocaleString()} ₽` : '—'}</td>
                      <td style={styles.td}><ChevronRight size={16} color="#94a3b8" /></td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  glassCard: { background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderRadius: 24, border: '1px solid rgba(255,255,255,0.6)', boxShadow: '0 12px 40px rgba(31,38,135,0.06)', overflow: 'hidden' },
  kpiCard: { background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(20px)', borderRadius: 20, padding: 20, border: '1px solid rgba(255,255,255,0.6)', boxShadow: '0 8px 24px rgba(31,38,135,0.04)', display: 'flex', alignItems: 'center', gap: 16 },
  kpiIcon: { width: 48, height: 48, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  kpiLabel: { fontSize: 13, color: '#64748b', fontWeight: 600, marginBottom: 4 },
  kpiValue: { fontSize: 24, fontWeight: 800, color: '#1a1a2e' },
  filterBtn: { padding: '6px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13, transition: 'all 0.2s' },
  selectStyle: { padding: '8px 16px', borderRadius: 12, border: '1px solid rgba(255,255,255,0.6)', background: 'rgba(255,255,255,0.5)', backdropFilter: 'blur(10px)', fontSize: 14, outline: 'none', color: '#1a1a2e' },
  th: { textAlign: 'left', padding: '14px 20px', borderBottom: '1px solid rgba(0,0,0,0.05)', color: '#64748b', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 },
  td: { padding: '14px 20px', borderBottom: '1px solid rgba(0,0,0,0.03)', fontSize: 14, color: '#1a1a2e' },
  badge: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 12, fontSize: 13, fontWeight: 600 },
};

export default Vacancies;
